import { useState, useEffect, useMemo, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import SchedulesTable from '../components/tables/SchedulesTable'
import EmptyListView from '../components/views/EmptyListView'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import rescheduleIcon from '../assets/reschedule-icon.png'
import rejectedIcon from '../assets/rejected-icon.png'
import inCheckIcon from '../assets/in-check-icon.png'
import confirmedIcon from '../assets/confirmed-icon.png'
import noSchedules from '../assets/no-schedules.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const Schedules = () => {
    const [schedules, setSchedules] = useState([])
    const [services, setServices] = useState([])
    const [nextSchedules, setNextSchedules] = useState([])
    const [pastSchedules, setPastSchedules] = useState([])
    const [showNextSchedules, setShowNextSchedules] = useState(true)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.CLIENT) {
                navigate('/')
            } else {
                getInterventionTypes()
                getSchedules()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get intervetion types request
    const getInterventionTypes = () => {
        fetch('/api/Schedule/Types', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                setServices(jsonRes)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getIntervetionTypesError)

                    setServices([])
                }, 1000)
     
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                setServices([])
            }, 1000)
        })
    }

    // Handle get schedules request
    const getSchedules = () => {
        fetch(`/api/Schedule/${loggedUser.userId}/List`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
                  
                    setSchedules(jsonRes)
                    splitArrayNextPast(jsonRes)
                } else {
                    showErrorToast(strings.getSchedulesError)
                    setSchedules([])
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                setSchedules([])
            }, 1000)
        })
    }

    // Splits the schedule array into next and past schedules
    const splitArrayNextPast = (arraySchedules) => {
        const today = new Date()

        if(arraySchedules.length > 0) {
            // Filter next and past schedules
            let nextSchedulesAux = []
            let pastSchedulesAux = []

            arraySchedules.forEach(element => {
                let currentDate

                if(element.endDate) {
                    currentDate = new Date(element.endDate)
                } else if(element.startDate) {
                    currentDate = new Date(element.startDate)
                } else {
                    currentDate = new Date(element.accessFacilitiesStartDate)
                }

                if(currentDate >= today) {
                    nextSchedulesAux.push(element)
                } else {
                    pastSchedulesAux.push(element)
                }
            })

            setNextSchedules(nextSchedulesAux.sort((a, b) => {
                let start
                let end

                if(a.scheduleTypeId === Constants.ScheduleTypes.FACILITIES) {
                    start = a.accessFacilitiesStartDate
                } else {
                    start = a.startDate
                }

                if(b.scheduleTypeId === Constants.ScheduleTypes.FACILITIES) {
                    end = b.accessFacilitiesStartDate
                } else {
                    end = b.startDate
                }

                return new Date(start) - new Date(end)
            }))

            if(nextSchedulesAux.length === 0) {
                setShowNextSchedules(false)
            }

            setPastSchedules(pastSchedulesAux.sort((a, b) => {
                let start
                let end

                if(a.scheduleTypeId === Constants.ScheduleTypes.FACILITIES) {
                    start = a.accessFacilitiesStartDate
                } else {
                    start = a.startDate
                }

                if(b.scheduleTypeId === Constants.ScheduleTypes.FACILITIES) {
                    end = b.accessFacilitiesStartDate
                } else {
                    end = b.startDate
                }

                return new Date(start) - new Date(end)
            }))
        }
    }

    // Handles date formatting
    const formatDate = (timeStamp) => {
        const date = new Date(timeStamp)
        const day = date.getDate()
        const month = Constants.MONTHS[date.getUTCMonth()]
        const year = date.getUTCFullYear()
        return `${day} de ${month} de ${year}`
    }

    // Handles time formatting
    const formatTime = (timeStamp) => {
        const date = new Date(timeStamp)
        const hours = date.getHours()
        const minutes = date.getMinutes()
        return `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes} h`
    }

    // Check all the dates and return first date time
    const getStartTime = (schedule) => {
        if(schedule.scheduleTypeId === Constants.ScheduleTypes.FAIRING || schedule.scheduleTypeId === Constants.ScheduleTypes.FAIRINGENERGY) {
            let datesArray = []

            if(schedule.dryStartDate) {
                datesArray.push(new Date(schedule.dryStartDate))
            }

            if(schedule.swimStartDate) {
                datesArray.push(new Date(schedule.swimStartDate))
            }

            if(schedule.lineChangeStartDate) {
                datesArray.push(new Date(schedule.lineChangeStartDate))
            }

            if(schedule.supplyPersonnelStartDate) {
                datesArray.push(new Date(schedule.supplyPersonnelStartDate))
            }

            if(schedule.rippingStartDate) {
                datesArray.push(new Date(schedule.rippingStartDate))
            }

            const dates = datesArray.map(date => date.getTime())

            const minTimestamp = Math.min(...dates)
            const firstDate = new Date(minTimestamp)
            
            if(minTimestamp === Infinity) {
                return '-'
            } else {
                return formatTime(firstDate)
            } 
        } else if (schedule.scheduleTypeId === Constants.ScheduleTypes.FACILITIES) {
            return formatTime(schedule.accessFacilitiesStartDate)
        } else {
            return '-'
        }
    }

    const columnsNext = useMemo(() => [
        {
            name: strings.date,
            selector: row => row.startDate ? row.startDate : row.accessFacilitiesStartDate,
            format: row => row.startDate ? formatDate(row.startDate) : formatDate(row.accessFacilitiesStartDate),
            sortable: true
        },
        {
            name: strings.time,
            selector: row => row.startDate,
            format: row => getStartTime(row),
            sortable: true
        },
        {
            name: strings.boat,
            selector: row => row.boat.name,
            sortable: true
        },
        {
            name: strings.registration,
            selector: row => row.boat.registration,
            sortable: true
        },
        {
            name: strings.service,
            selector: row => showServiceText(services, row.scheduleTypeId),
            sortable: true
        },
        {
            name: strings.state,
            selector: row => row.scheduleStatusId,
            cell: row => stateCell(row.scheduleStatusId),
            sortable: true
        }
    ], [services])

    const columnsPast = useMemo(() => [
        {
            name: strings.date,
            selector: row => row.startDate ? row.startDate : row.accessFacilitiesStartDate,
            format: row => row.startDate ? formatDate(row.startDate) : formatDate(row.accessFacilitiesStartDate),
            sortable: true
        },
        {
            name: strings.time,
            selector: row => row.startDate,
            format: row => getStartTime(row),
            sortable: true
        },
        {
            name: strings.boat,
            selector: row => row.boat.name,
            sortable: true
        },
        {
            name: strings.registration,
            selector: row => row.boat.registration,
            sortable: true
        },
        {
            name: strings.service,
            selector: row => showServiceText(services, row.scheduleTypeId),
            sortable: true
        },
    ], [services])

    const stateCell = (state) => {
        return (
            <div className='h-[1.563rem] w-[9.125rem] flex flex-row p-1 rounded items-center' style={{ backgroundColor: state === Constants.ScheduleStatus.REJECTED ? 'rgba(232, 95, 92, 0.10)' : state === Constants.ScheduleStatus.RESCHEDULE ? 'rgba(243, 146, 55, 0.10)' : state === Constants.ScheduleStatus.INCHECK ? 'rgba(0, 159, 227, 0.10)' : 'rgba(38, 171, 147, 0.10)' }}>
                <img className='h-4 w-4 me-2' src={state === Constants.ScheduleStatus.RESCHEDULE ? rescheduleIcon : state === Constants.ScheduleStatus.REJECTED ? rejectedIcon : state === Constants.ScheduleStatus.INCHECK ? inCheckIcon : confirmedIcon} alt='Icon Estado Agendamento'/>
                <p className='font-montserrat text-sm font-semibold' style={{ color: state === Constants.ScheduleStatus.REJECTED ? '#E85F5C' : state === Constants.ScheduleStatus.RESCHEDULE ? '#F39237' : state === Constants.ScheduleStatus.INCHECK ? '#009FE3' : '#26AB93'}}>
                    {state === Constants.ScheduleStatus.RESCHEDULE ? strings.reschedule : state === Constants.ScheduleStatus.REJECTED ? strings.rejected : state === Constants.ScheduleStatus.INCHECK ? strings.inCheck : strings.confirmed}
                </p>
            </div>
        )
    }

    // Handle show next schedules
    const handleShowNextSchedules = () => {
        if(!showNextSchedules) {
            setShowNextSchedules(true)
        }
    }

    // Handle show past schedules
    const handleShowPastSchedules = () => {
        if(showNextSchedules) {
            setShowNextSchedules(false)
        }
    }

    // Check the service and show matching text
    const showServiceText = (services, scheduleTypeId) => {
        const service = services && services.find((service) => service.id === scheduleTypeId)
        return service ? service.name : strings.accessToFacilities
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='h-14 border-b border-accent flex flex-row items-center px-[12.5rem] space-x-9'>
                <p className='font-montserrat font-semibold text-accent'>{strings.schedules}</p>
                <Link to='/boats' className='font-montserrat text-neutral5 cursor-pointer'>{strings.boats}</Link>
            </div>

            <div className='px-[12.5rem] min-h-screen mb-6'>
            
                { schedules.length === 0 ? null :
                    <div className='flex items-center justify-between mt-12'>

                        <div className='flex flex-row items-center space-x-6'>
                            <button onClick={handleShowNextSchedules} className='h-10 w-[13.313rem] rounded-[6.25rem] bg-pastel-blue' style={{ backgroundColor: showNextSchedules ? '#D6F3FF' : '#F0F3F5' }}>
                                <p className='font-montserrat' style={{ color: showNextSchedules ? '#007EB4' : '#232323', fontWeight: showNextSchedules ? '600' : '400' }}>{strings.nextSchedules}</p>
                            </button>

                            <button onClick={handleShowPastSchedules} className='h-10 w-[13.313rem] rounded-[6.25rem]' style={{ backgroundColor: showNextSchedules ? '#F0F3F5' : '#D6F3FF' }}>
                                <p className='font-montserrat' style={{ color: showNextSchedules ? '#232323' : '#007EB4', fontWeight: showNextSchedules ? '400' : '600' }}>{strings.past}</p>
                            </button>
                        </div>

                        <div className='flex w-[13.313rem]'>
                            <RoundedButtonLink 
                                path='/new-schedule'
                                text={strings.newSchedule}
                                height={40}
                                bgColor='#007EB4'
                                flex={true}
                            />
                        </div>

                    </div>
                }

                <div className='mt-8'>

                    { isLoading ?
                        <LoadingModal />
                    :   <>
                            { schedules.length > 0 ?
                                showNextSchedules ?
                                    <SchedulesTable 
                                        columns={columnsNext}
                                        data={nextSchedules}
                                        onClick={(item) => navigate(`/schedule-details/${item.schedulingId}`)}
                                        noData={strings.noNextSchedules}
                                    />
                                :   <SchedulesTable 
                                        columns={columnsPast}
                                        data={pastSchedules}
                                        onClick={(item) => navigate(`/schedule-details/${item.schedulingId}`)}
                                        noData={strings.noPastSchedules}
                                    />
                                
                            :   <EmptyListView 
                                    image={noSchedules}
                                    message={strings.noSchedules}
                                    path='/new-schedule'
                                    buttonText={strings.newSchedule}
                                />
                            }
                        </>
                    }

                </div>

            </div>

            <ToastContainer />
        </>
    )
}

export default Schedules